import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from "react"
import useApi from "modules/activities"
import { useActiveContentContext } from "contexts/ActiveContentContext"
import useLibraryApi from "modules/library"
import usePlacementApi from "modules/placements"
import useStreakApi from "modules/streaks"
import {
  newRightAnswer as correctAnswer,
  examSend,
  newWrongAnswer as wrongGrade,
  streakWin,
  streakLost,
  accountSetupAudios,
  getStartedSound,
} from "utils/sounds"
import useSound from "use-sound"
import {
  isActivityGradedByTeacher,
  isExplanationActivity,
  isRandomExerciseActivity,
} from "pages/student/activity/common/functions"
import { isRunningFromIframe, serializeQueryParams } from "common/functions"
import {
  ACTIVITY_TYPES,
  ACTIVITY_VIEW_MODES,
  ANSWER_TYPES,
  MODULE_ASSIGN_TYPES,
  MODULE_STREAK_EXERCISE,
  MODULE_TYPES,
} from "pages/student/activity/common/constants"
import { GROUP_TYPES, ENABLE_ACCOUNT_SETUPS_AUDIOS } from "common/constants"
import { PLACEMENT_STATUS_LOG_TYPES } from "pages/student/placements/common/constants"
import { Box } from "@mui/system"
import UploadProgress from "components/Activity/UploadProgress"
import i18n, { t } from "i18next"
import { usePlacementContext } from "./PlacementContext"
import Swal from "sweetalert2"
import withReactContent from "sweetalert2-react-content"
import { theme } from "themes/default/theme"
import useTour, { GET_STARTED, isTourForced } from "hooks/useTour"
import {
  gradeBarSteps,
  gradeWrong,
  retakSteps,
} from "components/Activity/GradeBar/GradeBar.GetStarted"
import { clearAllBodyScrollLocks } from "body-scroll-lock"
import { Beforeunload } from "react-beforeunload"
import { useGlobalAlert } from "./GlobalAlertContext"
import { useAuthContext } from "./AuthContext"
import PropTypes from "prop-types"
import { Button, Dialog, DialogContent, Stack, Typography } from "@mui/material"
import VolumeUpIcon from "@mui/icons-material/VolumeUp"
import Lottie from "react-lottie"
import PlacementLevel from "pages/student/placements/level"
import FinishTest from "components/PlacementTest/FinishTest"
import LoaderModal from "components/UI/LoaderModal"
import {
  bgOptions,
  fontFamilyOptions,
  fontOptions,
} from "common/textEditorConstants"
import { LANGUAGES_ASSETS } from "../services/config"

const cleanupAudio = (audio) => {
  if (audio) {
    try {
      audio.pause()
      audio.src = ""
      audio.removeAttribute("src")
      audio.load()
    } catch (e) {
      console.warn("Error cleaning up audio:", e)
    }
  }
}

const STREAK_VERSION = 2
const styles = {
  bg: {
    position: "fixed",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    background: "rgba(51, 51, 51, 0.25)",
    p: 6,
    borderRadius: 2.5,
    outline: "none",
  },
  streakModal: {
    position: "absolute",
    bottom: 80, //
    left: "50%",
    transform: "translateX(-50%)",
    m: 0,
    backgroundColor: "#424242",
    color: "white",
    borderRadius: "16px",
    padding: "8px 16px",
    minWidth: "250px",
    maxWidth: "90%",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  streakContent: {
    p: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  smashyStreakImage: {
    width: 200, // Ajusta el tamaño de la imagen
    height: 200,
    objectFit: "contain",
    mb: 1, // Margen inferior para separar de la línea de texto/audio
  },
  streakInfo: {
    display: "flex",
    alignItems: "center",
    gap: 1, // Espacio entre el icono y el texto
    mt: 0.5, // Pequeño margen superior si es necesario
  },
  streakAudio: {
    fontSize: 25,
    cursor: "pointer",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    p: 0.5,
  },
  streakText: {
    fontWeight: "bold",
  },
  alertTitle: {
    color: "#595959",
    padding: ".8em 1em 0",
    fontSize: "1.875em",
    textAlign: "center",
    textTransform: "none",
    wordWrap: "break-word",
    display: "block",
    maxWidth: "100%",
    fontWeight: "600",
    lineHeight: "1.28571429em",
    fontFamily: "Poppins Regular",
  },
  alertMessage: {
    color: "#545454",
    padding: "1em 1.6em .3em",
    fontSize: "1.125em",
    lineHeight: "normal",
    textAlign: "center",
    textTransform: "none",
    wordWrap: "break-word",
    fontFamily: "Poppins Regular",
  },
}

export const ModuleContext = React.createContext()

export const useModuleContext = () => {
  // get the context
  const context = useContext(ModuleContext)
  // if the context is undefined, throw an error
  if (context === undefined) {
    throw new Error("useModuleContext must be used within a ModuleProvider")
  }
  // return the context
  return context
}

const ModuleProvider = ({
  children,
  sx = {},
  viewMode: view = ACTIVITY_VIEW_MODES.STUDENT,
}) => {
  const [viewMode, setViewMode] = useState(view)
  const {
    selectedGroupData,
    audioSettings,
    userData,
    isPlacementAssignation,
    accountType,
    textEditorSettings,
  } = useAuthContext()
  const [showBadge, setShowBadge] = useState(false)
  const [module, setModule] = useState({
    grade: 0,
    repeat: 0,
  })
  const [mission, setMission] = useState()
  const [activities, setActivities] = useState([])
  const [studentAnswers, setStudentAnswers] = useState(null)
  const [activityIndex, setActivityIndex] = useState(-1)
  const [exerciseIndex, setExerciseIndex] = useState(-1)
  const [activity, setActivity] = useState({
    activityId: -1,
    activityTypeId: 0,
    exercises: [],
    accentsAndPunctuation: false,
  })
  const [exercise, setExercise] = useState({})
  const {
    getExercises,
    setGrade,
    getActivities,
    uploadGradeFile,
    addPartialGrade,
    addPartialGradeAll,
    deletePartialGrade,
    getActivityTeam,
    setUserAlert,
    isFetching,
  } = useApi()
  const { getActivityContent, getExercises: getExercisesBase } = useLibraryApi()
  const {
    getActivityContent: getActivityContentPlacement,
    getExercises: getExercisesPlacement,
    addGrade: addPlacementGrade /*, addTest */,
    addStatusLog: addPlacementStatus,
    checkDetails,
  } = usePlacementApi()
  // const { logSession } = useSession()
  const [playContinue] = useSound(getStartedSound, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [playCorrect] = useSound(correctAnswer, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [playIncorrect] = useSound(wrongGrade, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [playExamSend] = useSound(examSend, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [playStreakWin] = useSound(streakWin, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [playStreakLost] = useSound(streakLost, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const [isLoadingContent, setIsLoadingContent] = useState(false)
  const [disabled, setDisabled] = useState(false)
  const [uploadAudioProgress, setUploadAudioProgress] = useState(0)
  const [showCongratsModal, setShowCongratsModal] = React.useState(false)
  const [showStreakModal, setShowStreakModal] = React.useState(false)
  const [moduleStreakActivity, setModuleStreakActivity] = React.useState(0)
  const [moduleStreakExercise, setModuleStreakExercise] = React.useState(0)
  const [moduleImageId, setModuleImageId] = React.useState(1)
  const [prevGoToNextExercise, setPrevGoToNextExercise] = React.useState(false)
  const [isExamModule, setIsExamModule] = React.useState(false)
  const [gamesOpen, setGamesOpen] = React.useState(false)
  const { setActiveContent } = useActiveContentContext()
  const {
    setIsOpen,
    isGetStartedViewed,
    setGetStartedViewed,
    setSteps,
    setCurrentStep,
  } = useTour()
  // placement states
  const {
    placementTestId,
    placementLanguage,
    setPlacementLanguage,
    createPlacementTest,
    placementModules,
    finishPlacementTest,
    setPlacementModules,
    updatePlacementTest,
    placementIsFinisihed,
    placementFirstLoadFinished,
    // resumeData,
    moduleIdSetted,
    updateModulesStatus,
    prevRoute,
    getPlacementExpectedLevel,
    allowLevelSkip,
    setAllowLevelSkip,
  } = usePlacementContext()
  const MySwal = withReactContent(Swal)
  const [showGradeCount, setShowGradeCount] = useState(0)
  const [teamActivityData, setTeamActivityData] = useState(null)
  const { showAlert } = useGlobalAlert()
  const [showStrike, setShowStrike] = useState(false)
  const [streakAnimation, setStreakAnimation] = useState(null)
  const moduleStreakExerciseRef = useRef(0)
  const { setStreak, getStreakImage } = useStreakApi()
  const [streakItemId, setStreakItemId] = useState(null)
  const loadingStreakRef = useRef(false)
  const [showPlacementAlert, setShowPlacementAlert] = useState(false)
  const [showPlacementFinishedAlert, setShowPlacementFinishedAlert] =
    useState(false)
  const placementAlertAudioRef = useRef()
  const [placementExpectedLevel, setPlacementExpectedLevel] = useState(null)
  const [textFontSize, setTextFontSize] = useState()
  const [textFontStyle, setTextFontStyle] = useState()
  const [bgColor, setBgColor] = useState()
  const [allowAutoGrade, setAllowAutoGrade] = useState(false)
  const streakBusyRef = useRef(false)
  const moduleNeedsUpdate = useRef(true)

  useEffect(() => {
    if (textEditorSettings?.fontSize >= 0) {
      const selectedFontSize = fontOptions[textEditorSettings?.fontSize]
      if (selectedFontSize) {
        setTextFontSize(selectedFontSize.fontSize)
      }
    }
    if (textEditorSettings?.backgroundColor >= 0) {
      const selectedBgColor = bgOptions[textEditorSettings?.backgroundColor]
      if (selectedBgColor) {
        setBgColor(selectedBgColor.bg)
      }
    }
    if (textEditorSettings?.fontFamily >= 0) {
      const selectedFontFamily =
        fontFamilyOptions[textEditorSettings?.fontFamily]
      setTextFontStyle(selectedFontFamily?.name || fontFamilyOptions[0].name)
    }
  }, [textEditorSettings])

  useEffect(() => {
    window.openTestHelp = () => {
      setCurrentStep(0)
      setSteps(gradeBarSteps(nextExercise))
      setTimeout(() => {
        setSteps(gradeBarSteps(nextExercise))
        setIsOpen(true)
      }, 500)
    }
    window.openTestHelpRetake = () => {
      setCurrentStep(0)
      setSteps(retakSteps(nextExercise))
      setTimeout(() => {
        setSteps(retakSteps(nextExercise))
        setIsOpen(true)
      }, 500)
    }
    return () => {
      cleanupAudio(placementAlertAudioRef.current)
    }
  }, [])

  useEffect(() => {
    if (viewMode === ACTIVITY_VIEW_MODES.EXPLANATION) return
    if (
      activity &&
      activity?.activityId > 0 &&
      exercise &&
      exercise?.exerciseId > 0
    ) {
      // stop tour and reset page body
      setIsOpen(false)
      clearAllBodyScrollLocks()
      document.body.style.overflow = "auto"
      // set active content
      setActiveContent({
        relatedId: activity?.moduleActivityId || null,
        subRelatedId: exercise?.exerciseId || null,
        screen: "content",
      })
      const queryString = serializeQueryParams({
        activityId: activity.activityId,
        exerciseId: exercise.exerciseId,
      })
      const runningFromIframe = isRunningFromIframe()
      if (
        runningFromIframe &&
        typeof window?.parent?.onNavigate === "function"
      ) {
        window?.parent?.onNavigate(queryString)
      }
    }
  }, [activity, exercise])

  useEffect(() => {
    const isExam =
      module?.assignedTypeId === MODULE_ASSIGN_TYPES.EXAM ||
      (viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
        !placementFirstLoadFinished)
    setIsExamModule(isExam)
    setAllowAutoGrade(!isExam)
    if (
      viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
      !placementFirstLoadFinished
    ) {
      // allow if account type is kids
      setAllowAutoGrade(accountType < 7)
    }
    if (
      viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
      placementFirstLoadFinished
    ) {
      // check if current module is 100% completed
      setIsExamModule(false)
    }
    if (
      viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
      placementIsFinisihed
    ) {
      setIsExamModule(false)
    }
  }, [module, placementIsFinisihed])

  useEffect(() => {
    // each time the activityIndex changes, get exercises for that activity
    if (
      viewMode !== ACTIVITY_VIEW_MODES.EXPLANATION &&
      activityIndex >= 0 &&
      activityIndex < activities.length
      // && activities[activityIndex].exercises.length === 0
    ) {
      setIsLoadingContent(true)
      getActivityExercises({ shouldFindNextExercise: exerciseIndex < 0 })
    }
  }, [activityIndex])

  useEffect(() => {
    if (activityIndex >= 0) {
      const data = activities[activityIndex]
      if (
        data &&
        viewMode !== ACTIVITY_VIEW_MODES.STUDENT &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT &&
        viewMode !== ACTIVITY_VIEW_MODES.PLACEMENT_TEST
      ) {
        data.progress = 0
        data.grade = 0
      }
      setActivity(data)
    }
  }, [activities, activityIndex])

  useEffect(() => {
    if (exerciseIndex >= 0) {
      const newExercise = activity?.exercises[exerciseIndex]
      if (!newExercise) return
      if (
        viewMode !== ACTIVITY_VIEW_MODES.STUDENT &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_EXERCISE_GRADE &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY &&
        viewMode !== ACTIVITY_VIEW_MODES.PLACEMENT_TEST
      ) {
        newExercise.studentAnswer = null
      }
      setExercise(newExercise)
      if (
        viewMode === ACTIVITY_VIEW_MODES.STUDENT ||
        viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT ||
        viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY ||
        viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
      ) {
        const studentAnswer = newExercise?.studentAnswer
        setStudentAnswers(studentAnswer)
        if (
          viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
          placementIsFinisihed &&
          studentAnswer === null
        ) {
          const fixedAnswer = {
            grade: 0,
            answers: [
              {
                answer: "",
                status: false,
              },
            ],
            attemps: 1,
            exerciseId: newExercise.exerciseId,
            isAnswerGradedByTeacher: false,
          }
          setStudentAnswers(fixedAnswer)
          setExercise({
            ...newExercise,
            studentAnswer: fixedAnswer,
          })
        }
      } else {
        setStudentAnswers(null)
      }
    }
  }, [activity?.exercises, exerciseIndex])

  useEffect(() => {
    if (
      !loadingStreakRef.current &&
      !isLoadingContent &&
      studentAnswers !== undefined &&
      studentAnswers !== null &&
      !isExplanationActivity(activity?.activityTypeId) &&
      !isActivityGradedByTeacher(activity?.activityTypeId) &&
      !isExamModule &&
      viewMode !== ACTIVITY_VIEW_MODES.PLACEMENT_TEST
    ) {
      if (studentAnswers?.grade === 100) {
        setTimeout(() => {
          playCorrect()
        }, 300)
      } else {
        setTimeout(() => {
          // playIncorrect()
        }, 300)
      }
    }
  }, [studentAnswers, isLoadingContent, playCorrect, playIncorrect])

  useEffect(() => {
    moduleStreakExerciseRef.current = moduleStreakExercise
    // if (
    //   moduleStreakActivity === MODULE_STREAK_ACTIVITY ||
    //   moduleStreakExercise === MODULE_STREAK_EXERCISE
    // ) {
    //   if (moduleStreakActivity === MODULE_STREAK_ACTIVITY)
    //     setModuleStreakActivity(0)
    //   if (moduleStreakExercise === MODULE_STREAK_EXERCISE)
    //     setModuleStreakExercise(0)
    // }
  }, [moduleStreakActivity, moduleStreakExercise])

  useEffect(() => {
    let audioRef = null
    if (showStrike) {
      const audioURL = streakAnimation?.audioURL
      if (audioURL) {
        //  stopStreakWin()
        audioRef = new Audio(audioURL)
        audioRef
          .play()
          .catch((e) => console.error("Error al reproducir audio:", e))
        // when audio finish playing
        audioRef.onended = () => {
          cleanupAudio(audioRef)
          audioRef = null
          setTimeout(() => {
            loadingStreakRef.current = false
            setShowStrike(false)
            loadResources()
            setTimeout(() => {
              // go to next exercise
              nextExercise()
            }, 1000)
          }, 1000)
        }
      } else {
        // wait one second
        setTimeout(() => {
          loadingStreakRef.current = false
          setShowStrike(false)
          loadResources()
          nextExercise()
        }, 2000)
      }
    }
    return () => {
      if (audioRef) {
        cleanupAudio(audioRef)
      }
    }
  }, [showStrike, streakAnimation])

  useEffect(() => {
    if (prevGoToNextExercise && !showStreakModal) {
      setPrevGoToNextExercise(false)
      nextExercise()
    }
  }, [prevGoToNextExercise, showStreakModal])

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      typeof window.cleanupStaleAudios === "function"
    ) {
      window.cleanupStaleAudios()
    }
  }, [exercise?.exerciseId, activity?.activityId])

  useEffect(() => {
    if (streakItemId > 0) {
      // donwload image
      getStreakImage({
        languageId: userData?.language,
        streakItemId,
      }).then((response) => {
        const imageURL =
          response?.imageFileName?.length > 0
            ? `${response?.fullPath}/${response?.imageFileName}`
            : null
        const audioURL =
          response?.audioFileName?.length > 0
            ? `${response?.fullPath}/${response?.audioFileName}`
            : null
        const text = `${response?.name || ""}`
        // preload image
        if (imageURL !== null) {
          const img = new Image()
          img.src = imageURL
          img.onload = () => {
            console.log(`Imagen precargada: ${imageURL}`)
          }
          img.onerror = (error) => {
            console.error("Error al precargar la imagen:", error)
          }
        }
        if (audioURL) {
          // preload audio
          const audio = new Audio()
          audio.src = audioURL
          audio.preload = "auto"
          audio.oncanplaythrough = () => {
            console.log(`Audio precargado: ${audioURL}`)
          }
          audio.onerror = (error) => {
            console.error("Error al precargar el audio:", error)
          }
        }
        setStreakAnimation({
          imageURL,
          audioURL,
          text,
        })
      })
    }
  }, [streakItemId])

  useEffect(() => {
    if (streakBusyRef?.current) return
    if (selectedGroupData?.groupId > 0 && module?.moduleId > 0) {
      streakBusyRef.current = true
      loadResources()
    }
  }, [module?.moduleId, selectedGroupData?.groupId])

  // const backTo = (routeName) => {
  //   setModule(null)
  //   navigate(routeName, { replace: true })
  //   window.location.reload()
  // }

  const logSession = (event) => {
    if (process.env.REACT_APP_ENVIORNMENT !== "production") {
      console.log({ logSession: event })
    }
  }

  const loadResources = async () => {
    await new Promise((resolve) => setTimeout(resolve, 300))
    const curlang = module?.templateLanguageId
    const langName = {
      1: "es-MX",
      2: "en-US",
      3: "fr-FR",
      4: "es-MX",
    }
    if (STREAK_VERSION === 2) {
      setStreak({
        educationLevelId: selectedGroupData?.educationLevelId,
        learningLanguageId: selectedGroupData?.learningLanguageId,
        goal: MODULE_STREAK_EXERCISE,
      })
        .then((response) => {
          console.log({ response })
          setModuleStreakExercise(response?.progress || 0)
          setStreakItemId(response?.streakItemId)
        })
        .catch((ex) => {
          console.log(ex)
          setModuleStreakExercise(0)
        })
    } else {
      // donwload animation streak on every exercise changed
      //  fetch streak animation
      // get a random number bewteen 1 and 28
      const groupType =
        selectedGroupData?.groupTypeId > 0
          ? selectedGroupData?.groupTypeId
          : GROUP_TYPES.SECONDARY_REGULAR_CLASS
      // choose between 0 or 1
      const choose = Math.floor(Math.random() * 2)
      const animationType =
        groupType < 7 ? (choose === 0 ? "kids" : "kids2") : "teens"
      const maxAnimationsNumber = groupType < 7 ? (choose === 0 ? 28 : 46) : 20
      const streakIndex = Math.floor(Math.random() * maxAnimationsNumber) + 1
      fetch(
        `${process.env.REACT_APP_STREAK_ASSETS}/${animationType}/${langName[curlang]}/${streakIndex}.dot`
      )
        .then((f) => {
          f.json()
            .then((animationData) => {
              setStreakAnimation({
                animationData,
                index: streakIndex,
              })
            })
            .catch((ex) => {
              console.log("error loading json animation", ex)
            })
        })
        .catch((ex) => {
          console.log("error loading animation", ex)
        })
    }
  }

  const getActivityExercises = async ({
    shouldFindNextExercise = true,
    moduleId,
    activityId,
  }) => {
    // get exercises for the current activity
    try {
      const r = Math.floor(Math.random() * 28) + 1
      // await loadResources()
      setModuleImageId(r)
      let response = false
      switch (viewMode) {
        case ACTIVITY_VIEW_MODES.TEACHER_MODULE_BASE:
        case ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE:
          response = await getExercisesBase({
            moduleId: moduleId || module.moduleId,
            activityId: activityId || activities[activityIndex].activityId,
          })
          break
        case ACTIVITY_VIEW_MODES.PLACEMENT_TEST:
          response = await getExercisesPlacement({
            moduleId: moduleId || module.moduleId,
            activityId: activityId || activities[activityIndex].activityId,
            languageId: placementLanguage,
            placementTestId,
          })
          break
        default:
          response = await getExercises({
            missionModuleId: module.missionModuleId,
            activityId: activityId || activities[activityIndex].activityId,
          })
          break
      }
      if (response) {
        // if activity type is Picture and Question ,order exercises ramdomly else order by sequence
        const exercisesSorted = isRandomExerciseActivity(
          activities[activityIndex]?.activityTypeId
        )
          ? response.sort(() => Math.random() - 0.5)
          : response.sort((a, b) => a.sequence - b.sequence)
        setActivities((prev) => {
          const newActivities = [...prev]
          if (newActivities[activityIndex] !== undefined) {
            newActivities[activityIndex].exercises = exercisesSorted
          }
          return newActivities
        })
        // find first exercise that has not been completed
        if (shouldFindNextExercise) {
          const _exerciseIndex = exercisesSorted.findIndex(
            (exercise) => exercise.progress < 100
          )
          let _index = _exerciseIndex
          if (_exerciseIndex >= 0) {
            setExerciseIndex(_exerciseIndex)
          } else {
            if (exerciseIndex === -1) {
              // set last exercise
              setExerciseIndex(exercisesSorted.length - 1)
              _index = exercisesSorted.length - 1
            }
            if (exerciseIndex >= exercisesSorted.length) {
              setExerciseIndex(0)
              _index = 0
            }
          }
          const event = {
            view:
              viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
                ? "placement"
                : null,
            missionsActivityId: activities[activityIndex].missionsActivityId,
            exerciseId: activities[activityIndex].exercises[_index]?.exerciseId,
          }
          if (
            viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST ||
            viewMode === ACTIVITY_VIEW_MODES.STUDENT
          ) {
            logSession(event)
          }
        } else {
          let _index = exerciseIndex
          if (exerciseIndex === -1) {
            // set last exercise
            setExerciseIndex(exercisesSorted.length - 1)
            _index = exercisesSorted.length - 1
          }
          if (exerciseIndex >= exercisesSorted.length) {
            setExerciseIndex(0)
            _index = 0
          }
          const event = {
            view:
              viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
                ? "placement"
                : null,
            missionsActivityId: activities[activityIndex].missionsActivityId,
            exerciseId: activities[activityIndex].exercises[_index]?.exerciseId,
          }
          if (
            viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST ||
            viewMode === ACTIVITY_VIEW_MODES.STUDENT
          ) {
            logSession(event)
          }
        }
      }
    } catch (error) {
    } finally {
      setIsLoadingContent(false)
    }
  }

  const nextActivity = async (newModule = null) => {
    placementAlertAudioRef?.current?.pause()
    const runningFromIframe = isRunningFromIframe()
    if (activityIndex < activities.length - 1) {
      setActivityIndex(activityIndex + 1)
      setExerciseIndex(0)
      const event = {
        view:
          viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
            ? "placement"
            : "default",
        missionsActivityId: activities[activityIndex + 1].missionsActivityId,
        exerciseId: activities[activityIndex + 1].exercises[0]?.exerciseId,
      }
      if (
        viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST ||
        viewMode === ACTIVITY_VIEW_MODES.STUDENT
      ) {
        logSession(event)
      }
    } else {
      if (viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST) {
        console.log(
          "🚀 ~ nextActivity ~ placementModules:",
          placementModules,
          placementModules.length
        )
        if (placementIsFinisihed || moduleIdSetted) {
          const currentModuleIndex = placementModules.findIndex(
            (m) => m.moduleId === module?.moduleId
          )
          const nextModuleIndex = placementModules.findIndex(
            (m, index) => m.progress === 100 && index > currentModuleIndex
          )
          const nextModule = placementModules[nextModuleIndex]
          if (nextModuleIndex >= 0) {
            setModule(nextModule)
            setExerciseIndex(0)
            setIsLoadingContent(true)
            try {
              const newActivities = await updateModule(nextModule?.moduleId)
              // get exercises for the current activity
              await getActivityExercises({
                shouldFindNextExercise: false,
                activityId: newActivities[0]?.activityId,
                moduleId: nextModule?.moduleId,
              })
              setIsLoadingContent(false)
              setActivityIndex(0)
            } catch (error) {
              setIsLoadingContent(false)
            }
          } else {
            console.log("%c" + "Test has Finished", "color: green")
            console.log(placementIsFinisihed)
            if (!placementIsFinisihed) {
              const placementExpectedLevel = getPlacementExpectedLevel({
                allModules: placementModules,
              })
              console.log(
                "🚀 ~ nextActivity ~ placementExpectedLevel:",
                placementExpectedLevel
              )
              if (placementExpectedLevel?.isExpectedToBeCompleted) {
                MySwal.fire({
                  html: (
                    <Stack className="laoder-modal">
                      <LoaderModal />
                    </Stack>
                  ),
                  showCancelButton: false,
                  showConfirmButton: false,
                  allowEscapeKey: false,
                  allowOutsideClick: false,
                })
                setDisabled(true)
                finishPlacementTest({
                  module:
                    placementExpectedLevel?.module ||
                    placementModules[currentModuleIndex],
                  modules: placementModules,
                })
                  .then(() => {
                    MySwal.close()
                    setDisabled(false)
                    console.log("%c" + "Test Finished", "color: green")
                    playPlacementAlertAudio(
                      isPlacementAssignation
                        ? "placementTestCompleted"
                        : "diagnosticTestCompleted"
                    )
                    setShowPlacementFinishedAlert(true)
                  })
                  .catch((err) => {
                    console.log(
                      "🚀 ~ file: ModuleContext.js:512 ~ sendAnswer ~ err",
                      err
                    )
                  })
                return
              }
            }
            if (runningFromIframe && window.parent) {
              window.parent.location.href = `${window.location.origin}/${
                prevRoute || "missions"
              }`
            } else {
              window.location.href = `${window.location.origin}/${
                prevRoute || "missions"
              }`
            }
          }
          return
        }
        const moduleIndex = placementModules.findIndex(
          (m) => m.moduleId === module?.moduleId
        )

        console.log("🚀 ~ nextActivity ~ moduleIndex:", moduleIndex)
        if (moduleIndex < placementModules.length - 1) {
          const nextModule = placementModules[moduleIndex + 1]
          setModule(nextModule)
          setExerciseIndex(0)
          setIsLoadingContent(true)
          try {
            const newActivities = await updateModule(nextModule?.moduleId)
            // get exercises for the current activity
            await getActivityExercises({
              shouldFindNextExercise: false,
              activityId: newActivities[0]?.activityId,
              moduleId: nextModule?.moduleId,
            })
            setIsLoadingContent(false)
            setActivityIndex(0)
          } catch (error) {
            setIsLoadingContent(false)
          }
        } else {
          console.log("placementIsFinisihed:", placementIsFinisihed)
          if (!placementIsFinisihed) {
            console.log(
              "%c" + "Test has Finished; finish placement",
              "color: green"
            )
            console.log(placementModules[moduleIndex])
            const response = await getActivityContentPlacement({
              moduleId: placementModules[moduleIndex]?.moduleId,
              languageId: placementLanguage,
              placementTestId: placementTestId,
            })
            if (response?.data?.module !== null) {
              const placementExpectedLevel = getPlacementExpectedLevel({
                allModules: placementModules,
              })
              console.log(
                "🚀 ~ nextActivity ~ placementExpectedLevel:",
                placementExpectedLevel
              )
              if (placementExpectedLevel?.isExpectedToBeCompleted) {
                MySwal.fire({
                  html: (
                    <Stack className="laoder-modal">
                      <LoaderModal />
                    </Stack>
                  ),
                  showCancelButton: false,
                  showConfirmButton: false,
                  allowEscapeKey: false,
                  allowOutsideClick: false,
                })
                setDisabled(true)
                finishPlacementTest({
                  module:
                    placementExpectedLevel?.module ||
                    placementModules[moduleIndex],
                  modules: placementModules,
                })
                  .then(() => {
                    MySwal.close()
                    setDisabled(false)
                    console.log("%c" + "Test Finished", "color: green")
                    playPlacementAlertAudio(
                      isPlacementAssignation
                        ? "placementTestCompleted"
                        : "diagnosticTestCompleted"
                    )
                    setShowPlacementFinishedAlert(true)
                  })
                  .catch((err) => {
                    console.log(
                      "🚀 ~ file: ModuleContext.js:512 ~ sendAnswer ~ err",
                      err
                    )
                  })
                return
              }
            }
          }
          console.log("%c" + "Test has Finished", "color: green")
          if (runningFromIframe && window?.parent?.backToMissions) {
            window?.parent?.backToMissions({
              missionId: mission?.missionId,
              groupId: mission?.groupId,
            })
          } else {
            if (runningFromIframe && window.parent) {
              window.parent.location.href = `${window.location.origin}/${
                prevRoute || "missions"
              }`
            } else {
              window.location.href = `${window.location.origin}/${
                prevRoute || "missions"
              }`
            }
          }
        }
        return
      }
      if (viewMode !== ACTIVITY_VIEW_MODES.STUDENT) {
        return
      }
      console.log(`progress: `, newModule?.progress || module?.progress)
      if (newModule?.progress === 100 || module?.progress === 100) {
        setShowCongratsModal(true)
      } else {
        // find first activity with progress < 100
        const index = activities.findIndex(
          (activity) => activity.progress < 100
        )
        if (index >= 0) {
          setActivityIndex(index)
          setExerciseIndex(0)
        } else {
          setActivityIndex(0)
          setExerciseIndex(0)
        }
      }
    }
  }

  const prevActivity = async () => {
    const runningFromIframe = isRunningFromIframe()
    placementAlertAudioRef?.current?.pause()
    if (activityIndex > 0) {
      setActivityIndex(activityIndex - 1)
      setExerciseIndex(-1)
    } else {
      if (viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST) {
        if (placementIsFinisihed || moduleIdSetted) {
          const currentModuleIndex = placementModules.findIndex(
            (m) => m.moduleId === module?.moduleId
          )
          // find first module before current with progress = 100
          const prevModuleIndex = placementModules
            .slice(0, currentModuleIndex)
            .reverse()
            .findIndex((m) => m.progress === 100)
          const adjustIndex =
            prevModuleIndex >= 0 ? currentModuleIndex - 1 - prevModuleIndex : -1
          const prevModule = placementModules[adjustIndex]
          if (prevModuleIndex >= 0) {
            setModule(prevModule)
            setExerciseIndex(0)
            setIsLoadingContent(true)
            try {
              const newActivities = await updateModule(prevModule?.moduleId)
              // get exercises for the current activity
              await getActivityExercises({
                shouldFindNextExercise: false,
                activityId: newActivities[0]?.activityId,
                moduleId: prevModule?.moduleId,
              })
              setIsLoadingContent(false)
              setActivityIndex(0)
            } catch (error) {
              setIsLoadingContent(false)
            }
          } else {
            console.log("%c" + "Test has Finished", "color: green")
            if (runningFromIframe && window.parent) {
              window.parent.location.href = `${window.location.origin}/missions`
            } else {
              window.location.href = `${window.location.origin}/missions`
            }
          }
          return
        }
        if (runningFromIframe && window?.parent?.backToMissions) {
          window?.parent?.backToMissions({
            missionId: mission?.missionId,
            groupId: mission?.groupId,
          })
        } else {
          if (runningFromIframe && window.parent) {
            window.parent.location.href = `${window.location.origin}/${
              prevRoute || "missions"
            }`
          } else {
            window.location.href = `${window.location.origin}/${
              prevRoute || "missions"
            }`
          }
        }
      }
    }
  }

  const nextExercise = (newModule = null) => {
    placementAlertAudioRef?.current?.pause()
    const nextIndex = exerciseIndex + 1
    if (nextIndex < activities[activityIndex]?.exercises?.length) {
      setExerciseIndex((prev) => prev + 1)
      const event = {
        view:
          viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
            ? "placement"
            : "default",
        missionsActivityId: activities[activityIndex].missionsActivityId,
        exerciseId: activities[activityIndex].exercises[nextIndex]?.exerciseId,
      }
      if (
        viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST ||
        viewMode === ACTIVITY_VIEW_MODES.STUDENT
      ) {
        logSession(event)
      }
    } else {
      // if placement test check if current activity progress is 100
      // if not 100 go to the first exercise not completed
      // if is 100 go to the next exercise
      if (
        viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
        !placementFirstLoadFinished
      ) {
        if (activity.progress < 100) {
          // find first exercise that has not been completed
          const exerciseIdx = activities[activityIndex].exercises.findIndex(
            (exercise) => exercise.studentAnswer === null
          )
          if (exerciseIdx >= 0) {
            setExerciseIndex(exerciseIdx)
            return
          } else {
            console.log(
              "WARNING: activity progress is not 100 but could not find exercise not completed"
            )
          }
        }
      }
      nextActivity(newModule)
    }
  }

  const prevExercise = () => {
    placementAlertAudioRef?.current?.pause()
    const prevIndex = exerciseIndex - 1
    if (prevIndex >= 0) {
      setExerciseIndex((prev) => prev - 1)
      const event = {
        view:
          viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
            ? "placement"
            : "default",
        missionsActivityId: activities[activityIndex].missionsActivityId,
        exerciseId: activities[activityIndex].exercises[prevIndex]?.exerciseId,
      }
      if (
        viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST ||
        viewMode === ACTIVITY_VIEW_MODES.STUDENT
      ) {
        logSession(event)
      }
    } else {
      prevActivity()
    }
  }

  const onUploadProgress = (progressEvent) => {
    const { loaded, total } = progressEvent
    const percent = Math.floor((loaded * 100) / total)
    setUploadAudioProgress(percent)
    if (percent < 100) {
      console.log(`${loaded} bytes of ${total} bytes. ${percent}%`)
    }
  }

  const [waitUpload, setWaitUpload] = useState(false)

  useEffect(() => {
    if (waitUpload && uploadAudioProgress === 100) {
      setWaitUpload(false)
      setUploadAudioProgress(0)
      console.log(`finished.`)
    }
  }, [waitUpload, uploadAudioProgress])

  const sendAnswer = async ({
    grade,
    answers,
    autoSlide = true,
    files = [],
    filesFromPartialGrade = false,
    metadata,
    activityTeamId,
  }) => {
    try {
      // if (disabled) return;
      const waitForAnimation = false
      // const streakActivity = moduleStreakActivity + 1
      const streakExercise = moduleStreakExerciseRef?.current || 0
      let streakBroken = false
      if (grade >= 0) {
        setUploadAudioProgress(0)
        setDisabled(true)
        let response = false
        let testId = placementTestId
        const hasGrade = exercise?.studentAnswer?.attempts > 0
        const runningFromIframe = isRunningFromIframe()
        switch (viewMode) {
          case ACTIVITY_VIEW_MODES.STUDENT:
          case ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT:
            response = await setGrade({
              grade,
              answers,
              exerciseId: exercise.exerciseId,
              professorModuleActivityId: activity.professorModuleActivityId,
              metadata,
              filesFromPartialGrade,
              streak: hasGrade
                ? null
                : {
                    educationLevelId: selectedGroupData?.educationLevelId,
                    learningLanguageId: selectedGroupData?.learningLanguageId,
                    goal: MODULE_STREAK_EXERCISE,
                  },
              ...(activityTeamId !== undefined && { activityTeamId }),
            })
            break
          case ACTIVITY_VIEW_MODES.PLACEMENT_TEST:
            MySwal.fire({
              html: (
                <Stack className="laoder-modal">
                  <LoaderModal />
                </Stack>
              ),
              showCancelButton: false,
              showConfirmButton: false,
              allowEscapeKey: false,
              allowOutsideClick: false,
            })
            if (placementTestId === null || placementTestId < 0) {
              // add placement test
              const res = await createPlacementTest({
                levelObtained: module.moduleId,
                grade,
              })
              testId = res.placementTestId
            }
            if (
              runningFromIframe &&
              typeof window?.parent?.onPlacementPause === "function"
            ) {
              window.parent?.onPlacementPause()
            }
            // console.log("🚀 ~ file: ModuleContext.js:444 ~ testId:", testId)
            response = await addPlacementGrade({
              placementTestId: testId,
              exerciseId: exercise?.exerciseId,
              moduleActivityId: activity.moduleActivityId,
              grade,
              answers,
              files,
              loadDetails: true,
              filesFromPartialGrade,
            })
            break
          default:
            response = {
              success: true,
              data: {
                streak: null,
                moduleProgress: {
                  progress: 0,
                  grade: 0,
                },
                activityProgress: {
                  progress: 0,
                  grade: 0,
                },
                studentAnswer: {
                  grade,
                  answers,
                  attemps: 1,
                  exerciseId: exercise.exerciseId,
                  isAnswerGradedByTeacher: isActivityGradedByTeacher(
                    activity?.activityTypeId
                  ),
                },
              },
            }
            break
        }
        if (response?.success) {
          moduleNeedsUpdate.current = true
          response = response?.data
          console.log(" ~ sendAnswer", response)

          if (
            files.length > 0 &&
            (viewMode === ACTIVITY_VIEW_MODES.STUDENT ||
              viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT)
          ) {
            // send files
            setWaitUpload(true)
            const filesResponse = await uploadGradeFile({
              gradeId: response?.studentAnswer?.gradeId,
              files,
              onUploadProgress,
            })
            console.log("🚀 ~ filesResponse:", filesResponse)
            setWaitUpload(false)
            setUploadAudioProgress(0)
            if (filesResponse?.gradeId === response?.studentAnswer?.gradeId) {
              response.studentAnswer = filesResponse
            }
          }
          const moduleProgress = response.moduleProgress
          const activityProgress = response.activityProgress
          console.log({ mission })
          if (mission?.isFirstMission && moduleProgress.progress >= 5) {
            try {
              setUserAlert({ groupId: mission?.groupId })
              // update mission status
              setMission((prevMission) => ({
                ...prevMission,
                isFirstMission: false,
              }))
            } catch (error) {
              console.log("Error setting user alert:", error)
            }
          }
          if (
            module.moduleTypeId === MODULE_TYPES.GRAMMAR &&
            moduleProgress.progress === 100
          ) {
            setGetStartedViewed(GET_STARTED.FIRST_MODULE_GRAMMAR)
          }
          const newModule = {
            ...module,
            ...moduleProgress,
          }
          const newActivities = activities
          const exercises = newActivities[activityIndex].exercises
          // console.log("~ exercises:", exercises)
          newActivities[activityIndex].grade = activityProgress.grade
          newActivities[activityIndex].progress = activityProgress.progress
          if (viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_GRADE_EDIT) {
            newActivities[activityIndex].hasAnswersUpdatedByTeacher = true
          }
          if (exercises.length >= exerciseIndex + 1) {
            exercises[exerciseIndex].studentAnswer = response.studentAnswer
          }
          // get streak answer info
          if (
            !isExamModule &&
            response?.streak?.progress >= MODULE_STREAK_EXERCISE
          ) {
            loadingStreakRef.current = true
            setShowStrike(true) // if not exam module, show strike
            setStudentAnswers({
              ...response.studentAnswer,
              grade,
            })
            setExercise((prevExercise) => ({
              ...prevExercise,
              studentAnswer: response.studentAnswer,
            }))
            setModule(newModule)
            setActivities(newActivities)
            setModuleStreakExercise(MODULE_STREAK_EXERCISE)
            // play win racha
            playStreakWin()
            setDisabled(false)
            return
          }
          // console.log(streakExercise, response?.streak?.progress ,"currentStreal", MODULE_STREAK_EXERCISE - 2)
          if (
            response?.streak?.progress === 0 &&
            streakExercise >= MODULE_STREAK_EXERCISE - 2
          ) {
            streakBroken = true
            playStreakLost()
          }
          setModuleStreakActivity(0)
          if (response?.streak !== undefined && response?.streak !== null) {
            setModuleStreakExercise(response?.streak?.progress)
          }
          //
          if (isExamModule) {
            setTimeout(() => {
              playExamSend()
            }, 200)
            setStudentAnswers({
              ...response.studentAnswer,
              grade,
            })
            const newPlacementModules = [...placementModules]
            let editTestResult = null
            const canSkipLevel = allowLevelSkip
            if (viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST) {
              // update current module in placement modules
              const placementModuleIndex = newPlacementModules.findIndex(
                (m) => m.moduleId === module?.moduleId
              )
              console.log("placementModules", placementModuleIndex)
              if (placementModuleIndex >= 0) {
                console.log(
                  "placementModules",
                  newPlacementModules[placementModuleIndex]
                )
                // newModule.placementTestResultId = newPlacementModules[placementModuleIndex]?.placementTestResultId || 0;
                // newModule.placementTestStatusId = newPlacementModules[placementModuleIndex]?.placementTestStatusId || 0;
                console.log("placementModules", newModule)
                newPlacementModules[placementModuleIndex].grade =
                  newModule?.grade
                newPlacementModules[placementModuleIndex].progress =
                  newModule?.progress
                // if it is last activity and last exercise set module isDone
                if (
                  activityIndex === activities.length - 1 &&
                  exerciseIndex === exercises.length - 1
                ) {
                  newPlacementModules[placementModuleIndex].isDone = true
                }
              }
              setPlacementModules(newPlacementModules)
              const logResults = await updateModulesStatus({
                currentModuleIndex: placementModuleIndex,
                allModules: newPlacementModules,
                addLogs: false,
              })
              editTestResult = await updatePlacementTest({
                module: newModule,
                modules: logResults?.modules || newPlacementModules,
                logs: logResults?.logs || [],
              })
              if (
                editTestResult?.details?.placement?.allowLevelSkip !== undefined
              ) {
                setAllowLevelSkip(
                  editTestResult?.details?.placement?.allowLevelSkip
                )
              }
            }
            setTimeout(() => {
              const characterImage =
                accountType < 7
                  ? require("assets/img/auth/smashy.png")
                  : require("assets/img/auth/smashor.png")
              setModule(newModule)
              setActivities(newActivities)
              // if it is last exercise and last activity
              if (
                activityIndex === activities.length - 1 &&
                exerciseIndex === exercises.length - 1 &&
                viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
              ) {
                const placementModuleIndex = newPlacementModules.findIndex(
                  (m) => m.moduleId === module?.moduleId
                )
                updateModulesStatus({
                  currentModuleIndex: placementModuleIndex,
                  allModules: newPlacementModules,
                  addLogs: false,
                })
                  .then((statusResult) => {
                    const isExpectedToBeCompleted =
                      statusResult?.isExpectedToBeCompleted
                    setPlacementExpectedLevel(
                      statusResult?.expectedLevel || newModule
                    )
                    if (!isExpectedToBeCompleted) {
                      MySwal.close()
                      if (statusResult?.messageType !== undefined) {
                        playPlacementAlertAudio("placementLevelHard")
                        MySwal.fire({
                          html: (
                            <Box
                              sx={{
                                backgroundColor:
                                  bgColor || theme.palette.common.white,
                                maxHeight: 300,
                                overflow: "auto",
                              }}
                            >
                              <Typography
                                sx={[
                                  styles.alertTitle,
                                  {
                                    fontSize:
                                      parseInt(
                                        textFontSize?.replace("px", "")
                                      ) + 10,
                                    fontFamily: textFontStyle,
                                  },
                                ]}
                                variant="h2"
                              >
                                {statusResult?.messageType === "dropped"
                                  ? t(
                                      `this_level_looks_hard_${
                                        accountType < 7
                                          ? "elementary"
                                          : "secondary"
                                      }`
                                    )
                                  : t(
                                      `this_level_feels_tricky_${
                                        accountType < 7
                                          ? "elementary"
                                          : "secondary"
                                      }`
                                    )}
                              </Typography>
                              <Typography
                                sx={[
                                  styles.alertMessage,
                                  {
                                    fontSize:
                                      parseInt(
                                        textFontSize?.replace("px", "")
                                      ) - 1,
                                    fontFamily: textFontStyle,
                                  },
                                ]}
                                component="div"
                              >
                                {statusResult?.messageType === "dropped"
                                  ? t(
                                      `lets_try_an_easier_one_${
                                        accountType < 7
                                          ? "elementary"
                                          : "secondary"
                                      }`
                                    )
                                  : t(
                                      `ill_go_back_two_levels_${
                                        accountType < 7
                                          ? "elementary"
                                          : "secondary"
                                      }`
                                    )}
                              </Typography>
                            </Box>
                          ),
                          background: bgColor || theme.palette.common.white,
                          // title:
                          //   statusResult?.messageType === "dropped"
                          //     ? t(
                          //       `this_level_looks_hard_${accountType < 7 ? "elementary" : "secondary"
                          //       }`
                          //     )
                          //     : t(
                          //       `this_level_feels_tricky_${accountType < 7 ? "elementary" : "secondary"
                          //       }`
                          //     ),
                          // text:
                          //   statusResult?.messageType === "dropped"
                          //     ? t(
                          //       `lets_try_an_easier_one_${accountType < 7 ? "elementary" : "secondary"
                          //       }`
                          //     )
                          //     : t(
                          //       `ill_go_back_two_levels_${accountType < 7 ? "elementary" : "secondary"
                          //       }`
                          //     ),
                          imageAlt: "Smashy image",
                          imageWidth: 200,
                          imageHeight: 200,
                          confirmButtonColor: theme.palette.primary.main,
                          customClass: {
                            confirmButton: "swal-confirm-button",
                          },
                          imageUrl: characterImage,
                          confirmButtonText: t("continue"),
                          allowOutsideClick: false,
                        }).then((result) => {
                          placementAlertAudioRef?.current?.pause()
                          if (result.isConfirmed) {
                            playContinue()
                            onStartPlacementTest({
                              levelId: statusResult?.nextModuleId,
                              placementModules: statusResult?.modules,
                              updateModules: true,
                            })
                          }
                        })
                      } else {
                        if (canSkipLevel) {
                          setShowPlacementAlert(true)
                        } else {
                          // go to next module test
                          const nextLevel =
                            newPlacementModules[placementModuleIndex + 1]
                          onStartPlacementTest({
                            levelId: nextLevel.moduleId,
                            placementModules: newPlacementModules,
                          })
                        }
                      }
                    } else {
                      // finish placement test
                      finishPlacementTest({
                        module: statusResult?.expectedLevel || newModule,
                        activities: newActivities,
                        modules: statusResult?.modules,
                      })
                        .then(() => {
                          MySwal.close()
                          setDisabled(false)
                          console.log("%c" + "Test Finished", "color: green")
                          playPlacementAlertAudio(
                            isPlacementAssignation
                              ? "placementTestCompleted"
                              : "diagnosticTestCompleted"
                          )
                          setShowPlacementFinishedAlert(true)
                        })
                        .catch((err) => {
                          console.log(
                            "🚀 ~ file: ModuleContext.js:512 ~ sendAnswer ~ err",
                            err
                          )
                        })
                    }
                  })
                  .catch((error) => {
                    MySwal.close()
                    setDisabled(false)
                    const teacherImage =
                      selectedGroupData?.teachers?.[0]?.avatar
                    showAlert(
                      `${t(error?.message || "Please try again")}`,
                      teacherImage
                    )
                  })
              } else {
                MySwal.close()
                nextExercise(newModule)
                setDisabled(false)
              }
            }, 1000)
            return
          }
          if (isActivityGradedByTeacher(activity?.activityTypeId)) {
            playCorrect()
            setTimeout(() => {
              setStudentAnswers({
                ...response.studentAnswer,
                // grade,
              })
              setModule(newModule)
              setActivities(newActivities)
              nextExercise(newModule)
              setDisabled(false)
            }, 2500)
            return response.studentAnswer
          }
          setStudentAnswers({
            ...response.studentAnswer,
            grade,
          })
          if (autoSlide && grade !== ANSWER_TYPES.CORRECT && !streakBroken) {
            playIncorrect()
          }
          // wait 1 second for sound to finish
          setTimeout(async () => {
            setExercise((prevExercise) => ({
              ...prevExercise,
              studentAnswer: response.studentAnswer,
            }))
            setModule(newModule)
            setActivities(newActivities)
            // console.log("waiting for animation", waitForAnimation)
            setPrevGoToNextExercise(
              autoSlide && grade === ANSWER_TYPES.CORRECT && waitForAnimation
            )
            if (
              autoSlide &&
              grade === ANSWER_TYPES.CORRECT &&
              !waitForAnimation
            ) {
              setShowGradeCount(showGradeCount + 1)
              const firstGradeActivityDisplayed = await isGetStartedViewed(
                GET_STARTED.ACTIVITY_GRADED
              )
              if (!firstGradeActivityDisplayed) {
                setCurrentStep(0)
                setSteps(gradeBarSteps(nextExercise))
                setGetStartedViewed(GET_STARTED.ACTIVITY_GRADED)
                setTimeout(() => {
                  setSteps(gradeBarSteps(nextExercise))
                  setIsOpen(true)
                }, 500)
              } else {
                const firstRetakeActivityDisplayed = await isGetStartedViewed(
                  GET_STARTED.ACTIVITY_RETAKE
                )
                if (!firstRetakeActivityDisplayed && showGradeCount >= 2) {
                  setCurrentStep(0)
                  setSteps(retakSteps(nextExercise))
                  setGetStartedViewed(GET_STARTED.ACTIVITY_RETAKE)
                  setTimeout(() => {
                    setSteps(retakSteps(nextExercise))
                    setIsOpen(true)
                  }, 500)
                } else {
                  nextExercise(newModule)
                }
              }
            } else {
              if (
                isTourForced() &&
                activity?.activityTypeId !==
                  ACTIVITY_TYPES.PICTURE_AND_QUESTION_WRITE
              ) {
                setCurrentStep(0)
                setSteps(gradeWrong(nextExercise))
                setTimeout(() => {
                  setSteps(gradeWrong(nextExercise))
                  setIsOpen(true)
                }, 500)
              }
            }
            setDisabled(false)
          }, 1250)
          return response.studentAnswer
        } else {
          console.log("error in resposne", response?.message)
          setDisabled(false)
          const teacherImage = selectedGroupData?.teachers?.[0]?.avatar
          showAlert(
            `${t(response?.message || "Please try again")}`,
            teacherImage
          )
          return {
            canRepeat: true,
          }
        }
      }
    } catch (error) {
      setDisabled(false)
      setWaitUpload(false)
      setUploadAudioProgress(0)
    }
  }

  const playPlacementAlertAudio = (audioName) => {
    if (!ENABLE_ACCOUNT_SETUPS_AUDIOS) return
    const curlang = i18n?.language || "en-US"
    const folder =
      accountType < 7
        ? "elementary"
        : accountType < 13
        ? "secondary"
        : "university"
    const source = `${LANGUAGES_ASSETS}/sounds/createAccount/${curlang}/${folder}/${accountSetupAudios[audioName]}`
    if (placementAlertAudioRef.current) {
      cleanupAudio(placementAlertAudioRef.current)
    }
    const audio = new Audio(source)
    audio.play()
    placementAlertAudioRef.current = audio
  }

  const sendPartialAnswer = async ({
    answer = null,
    file,
    metadata,
    answers = null,
  }) => {
    try {
      if (
        viewMode === ACTIVITY_VIEW_MODES.STUDENT ||
        viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
      ) {
        const data = {
          answer,
          answers,
          exerciseId: exercise.exerciseId,
          professorModuleActivityId: activity.professorModuleActivityId,
          metadata,
          audioFile: file,
          placementTestId,
        }
        const response = await (answer !== null
          ? addPartialGrade(data)
          : addPartialGradeAll(data))
        if (response?.success) {
          return response?.data
        } else {
          console.log("error in resposne", response?.message)
          return {
            canRepeat: true,
          }
        }
      }
    } catch (error) {
      console.log(
        "🚀 ~ file: ModuleContext.js:978 ~ sendPartialAnswer ~ error:",
        error
      )
      setWaitUpload(false)
      setUploadAudioProgress(0)
    }
  }

  const solveActivity = async ({ grade, answers }) => {
    if (viewMode !== ACTIVITY_VIEW_MODES.STUDENT) {
      return
    }
    // send answer for each exercise in the activity
    const newActivities = activities
    const exercises = newActivities[activityIndex].exercises
    for (let i = 0; i < exercises.length; i++) {
      const exercise = exercises[i]
      const res = await setGrade({
        grade,
        answers,
        exerciseId: exercise.exerciseId,
        professorModuleActivityId: activity.professorModuleActivityId,
      })
      if (res?.success) {
        const response = res?.data
        exercises[i].studentAnswer = response.studentAnswer
        // if last exercise, update activity progress
        if (i === exercises.length - 1) {
          const moduleProgress = response.moduleProgress
          const activityProgress = response.activityProgress
          const newModule = {
            ...module,
            ...moduleProgress,
          }
          if (
            module.moduleTypeId === MODULE_TYPES.GRAMMAR &&
            moduleProgress.progress === 100
          ) {
            setGetStartedViewed(GET_STARTED.FIRST_MODULE_GRAMMAR)
          }
          newActivities[activityIndex].grade = activityProgress.grade
          newActivities[activityIndex].progress = activityProgress.progress
          setModule(newModule)
          setActivities(newActivities)
        }
      }
    }
  }

  const findActivity = async () => {
    // find first activity with exercises that has not been completed
    const activityIndex = activities.findIndex(
      (activity) => activity.progress < 100
    )
    if (activityIndex >= 0) {
      setActivityIndex(activityIndex)
    } else {
      // all activities are completed
      setExerciseIndex(0)
      setActivityIndex(0)
    }
  }

  const updateModule = async (moduleId = -1) => {
    // update module and activity progress
    let response = false
    switch (viewMode) {
      case ACTIVITY_VIEW_MODES.PLACEMENT_TEST:
        response = await getActivityContentPlacement({
          moduleId: moduleId,
          languageId: placementLanguage,
          placementTestId: placementTestId,
        })
        break
      case ACTIVITY_VIEW_MODES.TEACHER_MODULE_BASE:
      case ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE:
        response = await getActivityContent({ moduleId: module?.moduleId })
        break
      default:
        response = await getActivities({
          group: mission?.groupId,
          missionId: mission?.missionId,
          missionModuleId: module?.missionModuleId,
        })
        break
    }
    if (response) {
      const moduleData = response?.module || response?.data?.module
      if (viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST) {
        // find module in placement modules
        const placementModuleIndex = placementModules.findIndex(
          (m) => m.moduleId === moduleId
        )
        if (placementModuleIndex >= 0) {
          const newPlacementModules = [...placementModules]
          const modulePlacementTestResultId =
            newPlacementModules[placementModuleIndex]?.placementTestResultId
          const modulePlacementTestStatusId =
            newPlacementModules[placementModuleIndex]?.placementTestStatusId
          if (
            modulePlacementTestResultId === 0 ||
            modulePlacementTestStatusId === 0
          ) {
            const details = await checkDetails({ placementTestId })
            if (details?.placement?.grades?.length > 0) {
              const gradeModuleData = details.placement.grades.find(
                (g) => g.levelId === moduleId
              )
              if (gradeModuleData) {
                moduleData.placementTestResultId =
                  gradeModuleData.placementTestResultId
                moduleData.placementTestStatusId =
                  gradeModuleData.placementTestStatusId
                moduleData.grade = gradeModuleData?.grade
                moduleData.progress = gradeModuleData?.progress
              }
            }
          }
          if (
            !moduleData.placementTestResultId ||
            moduleData.placementTestResultId <= 0
          ) {
            moduleData.placementTestResultId = modulePlacementTestResultId
          }
          if (
            !moduleData.placementTestStatusId ||
            moduleData.placementTestStatusId <= 0
          ) {
            moduleData.placementTestStatusId = modulePlacementTestStatusId
          }
          newPlacementModules[placementModuleIndex] = moduleData
          setPlacementModules(newPlacementModules)
          console.log(
            "🚀 ~ updateModule ~ newPlacementModules:",
            newPlacementModules
          )
        }
      }
      setModule(moduleData)
      moduleNeedsUpdate.current = false
      // }
      // for each activity graded by the teacher load the exercises
      let activities = response?.activities || response?.data?.activities || []
      if (
        viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY ||
        viewMode === ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE
      ) {
        const activityId = activities[activityIndex].activityId
        activities = activities.filter((a) => a.activityId === activityId)
      }
      // check if there are matching activities that can be auto solved
      const countActivities = activities.filter((a) => a.isVisible).length
      if (countActivities > 0) {
        activities = activities.filter((a) => a.isVisible)
      }
      for (const activity of activities) {
        if (isActivityGradedByTeacher(activity?.activityTypeId)) {
          activity.exercises = await getExercises({
            missionModuleId: response?.module?.missionModuleId,
            activityId: activity.activityId,
          })
        }
      }
      setActivities(activities || [])
      return activities
    }
  }

  const updateActivityIndex = async (newActivityIndex) => {
    if (!isLoadingContent) {
      setIsLoadingContent(true)
      if (
        (moduleNeedsUpdate.current ||
          process.env.REACT_APP_ENVIORNMENT === "production") &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_MODULE_BASE &&
        viewMode !== ACTIVITY_VIEW_MODES.TEACHER_ACTIVITY_BASE
      ) {
        await updateModule(
          viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST
            ? module?.moduleId
            : -1
        )
      }
      if (activityIndex === newActivityIndex) {
        setExerciseIndex(0)
        await getActivityExercises({ shouldFindNextExercise: false })
      } else {
        setExerciseIndex(0)
        setActivityIndex(newActivityIndex)
      }
    }
  }

  const resetExercise = useCallback(async (partialGradeId) => {
    const response = await deletePartialGrade({
      partialGradeId: partialGradeId,
    })
    return response
  }, [])

  const startTeamActivity = async (joinTeam = false) => {
    try {
      const response = await getActivityTeam({
        exerciseId: exercise?.exerciseId,
        missionsActivityId: exercise?.missionsActivityId,
        groupId: selectedGroupData?.groupId,
        joinTeam,
      })
      if (response) {
        setTeamActivityData(response)
        return response
      }
    } catch (error) {
      console.log("🚀 ~ startTeamActivity ~ error:", error)
    }
    return null
  }

  const onStartPlacementTest = async ({
    levelId,
    levelName,
    levelDescription,
    placementModules = [],
    updateModules = false,
  }) => {
    MySwal.close()
    setDisabled(false)
    setShowPlacementAlert(false)
    if (updateModules) {
      setPlacementModules(placementModules)
    }

    const moduleIndex = placementModules.findIndex(
      (m) => m.moduleId === levelId
    )
    console.log("🚀 ~ nextActivity ~ moduleIndex:", moduleIndex)
    if (moduleIndex < 0) {
      console.log("%c" + "Module not found in placement modules", "color: red")
      return
    }
    if (moduleIndex < placementModules.length) {
      if (
        placementModules[moduleIndex].placementTestStatusId !==
          PLACEMENT_STATUS_LOG_TYPES.SUCCESS &&
        placementModules[moduleIndex].placementTestStatusId !==
          PLACEMENT_STATUS_LOG_TYPES.FAILED &&
        placementModules[moduleIndex].placementTestStatusId !==
          PLACEMENT_STATUS_LOG_TYPES.REASSIGNED
      ) {
        // add or update placement status to on progress
        await addPlacementStatus({
          placementTestResultId:
            placementModules[moduleIndex].placementTestResultId,
          statusId: PLACEMENT_STATUS_LOG_TYPES.ONPROGRESS,
        })
      }
      setModule(placementModules[moduleIndex])
      setExerciseIndex(0)
      setIsLoadingContent(true)
      try {
        const newActivities = await updateModule(
          placementModules[moduleIndex]?.moduleId
        )
        // get exercises for the current activity
        await getActivityExercises({
          shouldFindNextExercise: false,
          activityId: newActivities[0]?.activityId,
          moduleId: placementModules[moduleIndex]?.moduleId,
        })
        setIsLoadingContent(false)
        setActivityIndex(0)
      } catch (error) {
        setIsLoadingContent(false)
      }
    }
  }
  const testPlacement = () => {
    // set grade of current module for testing
    const newPlacementModules = [...placementModules]
    console.log("🚀 ~ newPlacementModules:", newPlacementModules)
    const moduleIndex = newPlacementModules.findIndex(
      (m) => m.moduleId === module?.moduleId
    )
    console.log("🚀 ~ moduleIndex:", moduleIndex, module)
    newPlacementModules[moduleIndex].grade = 25
    updateModulesStatus({
      currentModuleIndex: moduleIndex,
      allModules: newPlacementModules,
      addLogs: false,
    }).then((statusResult) => {
      console.log("🚀 ~ statusResult:", statusResult)
    })
  }

  const value = useMemo(
    () => ({
      module,
      mission,
      activity,
      exercise,
      activities,
      activityIndex,
      exerciseIndex,
      studentAnswers,
      loadingContent: isLoadingContent,
      isFetching: isFetching,
      disabled,
      showCongratsModal,
      showStreakModal,
      teamActivityData,
      showStrike,
      setTeamActivityData,
      setModule,
      setMission,
      setActivities,
      setExercise,
      setActivityIndex: updateActivityIndex,
      changeActivityIndex: setActivityIndex,
      setExerciseIndex,
      nextActivity,
      prevActivity,
      nextExercise,
      prevExercise,
      sendAnswer,
      sendPartialAnswer,
      setStudentAnswers,
      findActivity,
      solveActivity,
      moduleImageId,
      setModuleImageId,
      loadResources,
      setShowStreakModal,
      isExamModule,
      setActivity,
      viewMode,
      setShowCongratsModal,
      setPlacementLanguage,
      getActivityExercises,
      gamesOpen,
      setGamesOpen,
      setShowBadge,
      showBadge,
      resetExercise,
      moduleStreakExercise,
      startTeamActivity,
      setViewMode,
      allowAutoGrade,
    }),
    [
      module,
      mission,
      activity,
      exercise,
      activities,
      activityIndex,
      exerciseIndex,
      studentAnswers,
      isLoadingContent,
      disabled,
      isFetching,
      showCongratsModal,
      showStreakModal,
      moduleImageId,
      isExamModule,
      gamesOpen,
      showBadge,
      teamActivityData,
      viewMode,
      moduleStreakExercise,
      moduleStreakActivity,
      showStrike,
      allowAutoGrade,
    ]
  )

  return (
    <ModuleContext.Provider value={value}>
      {
        <Beforeunload
          onBeforeunload={() => {
            if (waitUpload) {
              return t(
                "The audios are still uploading, please wait until they finish."
              )
            }
          }}
        >
          <Box sx={sx} className={"module-context"}>
            {process.env.REACT_APP_ENVIORNMENT === "development" &&
              viewMode === ACTIVITY_VIEW_MODES.PLACEMENT_TEST &&
              module?.moduleId > 0 && (
                <Button
                  onClick={testPlacement}
                  variant="contained"
                  disableElevation
                  id="test-placement-button"
                  sx={{
                    position: "absolute",
                    top: 75,
                    left: 16,
                    zIndex: 1000,
                  }}
                >
                  test placement
                </Button>
              )}

            {children}
            {uploadAudioProgress > 0 && (
              <UploadProgress progress={uploadAudioProgress} />
            )}
          </Box>
        </Beforeunload>
      }
      <Dialog
        open={showPlacementFinishedAlert}
        openTest={module?.moduleId === 298378}
        disableEscapeKeyDown
        onClose={(evt, reason) => {
          if (reason !== "backdropClick") {
            placementAlertAudioRef.current?.pause()
            setShowPlacementFinishedAlert(false)
          }
        }}
        PaperProps={{
          sx: {
            maxWidth: "75%",
          },
        }}
      >
        <DialogContent>
          <FinishTest
            accountType={
              selectedGroupData?.groupTypeId > 0
                ? selectedGroupData?.groupTypeId
                : GROUP_TYPES.SECONDARY_NEWCOMERS_REGULAR_CLASS
            }
            onClose={() => {
              placementAlertAudioRef.current?.pause()
              setShowPlacementFinishedAlert(false)
            }}
            levelName={placementExpectedLevel?.moduleName || ""}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={showPlacementAlert}
        openTest={module?.moduleId === 294805}
        onClose={(evt, reason) => {
          if (reason !== "backdropClick") {
            setShowPlacementAlert(false)
          }
        }}
        PaperProps={{
          sx: {
            maxWidth: "75%",
          },
        }}
      >
        <DialogContent>
          <PlacementLevel
            open={showPlacementAlert}
            modules={placementModules}
            onStartLevel={onStartPlacementTest}
            viewMode={viewMode}
          />
        </DialogContent>
      </Dialog>
      {
        <Dialog
          onClose={() => setShowStrike(false)}
          open={
            showStrike &&
            streakAnimation !== null &&
            module?.progress !== 100 &&
            viewMode !== ACTIVITY_VIEW_MODES.PLACEMENT_TEST
          }
          // open={mission?.missionId === 561653}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0)", // Fondo transparente
            },
          }}
          PaperProps={{
            sx: styles.streakModal,
          }}
        >
          <DialogContent
            sx={[styles.streakContent, { backgroundColor: "#424242" }]}
          >
            <Box
              component="img"
              src={streakAnimation?.imageURL}
              alt="Smashy Character"
              sx={styles.smashyStreakImage}
            />
            <Box sx={styles.streakInfo}>
              {streakAnimation?.audioURL && (
                <VolumeUpIcon
                  sx={styles.streakAudio}
                  //  onClick={handlePlayAudio}
                />
              )}
              {streakAnimation?.text?.trim()?.length > 0 && (
                <Typography variant="body1" sx={styles.streakText}>
                  {streakAnimation?.text}
                </Typography>
              )}
            </Box>
          </DialogContent>
        </Dialog>
      }
      {STREAK_VERSION === 1 && (
        <Dialog
          onClose={() => setShowStrike(false)}
          open={showStrike && streakAnimation !== null}
        >
          <Box justifyContent={"center"} alignItems={"center"} sx={styles.bg}>
            <Lottie
              options={{
                loop: true,
                autoPlay: true,
                animationData: streakAnimation?.animationData,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              isStopped={false}
              isPaused={false}
              width={300}
              height={300}
            />
          </Box>
        </Dialog>
      )}
    </ModuleContext.Provider>
  )
}

ModuleProvider.propTypes = {
  children: PropTypes.node,
  sx: PropTypes.object,
  viewMode: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

export default ModuleProvider
